import { PageSuggestion, CollectionSuggestion, CreatePageSuggestion, CreateCollectionSuggestion } from '@/types';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { VariableSizeList } from 'react-window';
import { SuggestionButton } from '../editor/Suggestions';
import { InputDropdownProps, useArrowSelectableIndex } from './Dropdown';
import { PopOver } from './PopOver';
import ItemGroups from '../ItemGroups';

export type PagesCollectionsSuggestions =
    | PageSuggestion
    | CollectionSuggestion
    | CreatePageSuggestion
    | CreateCollectionSuggestion;

// Type for items passed to ItemGroups, augmenting original suggestions with a 'type' property
// An interface can only extend an object type or intersection of object types with statically known members.
// So we use an intersection type here.
type ItemGroupCompatibleSuggestion<Out extends PagesCollectionsSuggestions> = Out & {
    itemType: 'suggestionItem'; // Using itemType to avoid conflict if Out already has 'type'
};

export type Props<In extends PagesCollectionsSuggestions, Out extends PagesCollectionsSuggestions> = {
    onSubmit: (data: {
        search: string;
        selectedItem: Out;
        event: React.SyntheticEvent<Element, Event> | Event;
    }) => void;
    getSuggestions(): In[];
    filterBySearch: (suggestions: In[], search: string) => Out[];
    isOpen: boolean;
    onClose: () => void;
    attachToRef: React.MutableRefObject<HTMLElement | null>;
    dropdownProps?: InputDropdownProps<In>['dropdownProps'];
    inputProps?: InputDropdownProps<In>['inputProps'];
    label: string;
};

const SUGGESTION_MAX_HEIGHT = 350;

function LinkToPageDropDown<In extends PagesCollectionsSuggestions, Out extends PagesCollectionsSuggestions>({
    isOpen,
    onClose,
    onSubmit,
    getSuggestions,
    filterBySearch: filterSuggestionsFunc,
    inputProps,
    label,
}: Props<In, Out>) {
    const [search, setSearch] = useState('');
    const listRef = useRef<VariableSizeList>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
        if (!isOpen) {
            setSearch('');
        }
    }, [isOpen]);

    const allSuggestions = useMemo(() => getSuggestions(), [getSuggestions]);

    const filteredSuggestions = useMemo(() => {
        return filterSuggestionsFunc(allSuggestions, search);
    }, [allSuggestions, search, filterSuggestionsFunc]);

    const itemsForItemGroup = useMemo((): (Out & { type: 'suggestionItem' })[] => {
        return filteredSuggestions.map((suggestion) => ({
            ...suggestion,
            type: 'suggestionItem' as const,
        }));
    }, [filteredSuggestions]);

    const { index: selectedIndex, setIndex } = useArrowSelectableIndex({
        onSelect: (idx) => {
            const selectedOriginalItem = filteredSuggestions[idx];
            if (selectedOriginalItem) {
                onSubmit({
                    search,
                    selectedItem: selectedOriginalItem,
                    event: new Event('submit', { bubbles: true, cancelable: true }),
                });
                onClose();
            }
        },
        maxIndex: filteredSuggestions.length - 1,
        active: isOpen,
        selectWithTab: true,
        initialIndex: 0,
        onUpdate: (idx) => {
            if (listRef.current) {
                listRef.current.scrollToItem(idx, 'smart');
            }
        },
    });

    useEffect(() => {
        setIndex(0);
        if (listRef.current) {
            listRef.current.scrollToItem(0);
        }
    }, [search, itemsForItemGroup, setIndex]);

    const renderSuggestion = useCallback(
        (item: ItemGroupCompatibleSuggestion<Out>, itemIndex: number) => {
            const originalSuggestion = filteredSuggestions[itemIndex];

            return (
                <SuggestionButton
                    suggestion={originalSuggestion}
                    selected={selectedIndex === itemIndex}
                    onMouseEnter={() => setIndex(itemIndex)}
                    onClick={(event) => {
                        onSubmit({ search, selectedItem: originalSuggestion, event });
                        onClose();
                    }}
                    showIcons={true}
                >
                    {originalSuggestion.title.trim().length > 0 ? originalSuggestion.title : 'Untitled'}
                </SuggestionButton>
            );
        },
        [selectedIndex, setIndex, onSubmit, search, onClose, filteredSuggestions],
    );

    if (!isOpen) {
        return null;
    }

    return (
        <div className="flex flex-col h-full relative bg-white dark:bg-saga-gray-900 dark:border dark:border-saga-gray-800 pointer-events-auto rounded-md shadow-popupSmall w-highlight">
            <div className="">
                <PopOver.Input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    {...inputProps}
                    className="text-sm pl-3.5 pr-2 py-2 bg-saga-gray-100 dark:bg-saga-gray-800 focus:outline-0 font-normal placeholder-saga-text-gray block w-full appearance-none leading-normal ds-input pointer-events-auto rounded-t-md"
                />
            </div>
            {itemsForItemGroup.length > 0 && (
                <div className="flex-grow overflow-hidden px-1.5 scroll-on-hover-y">
                    <ItemGroups
                        ref={listRef}
                        items={itemsForItemGroup}
                        renderItem={renderSuggestion}
                        labels={{
                            suggestionItem:
                                label && itemsForItemGroup.length > 0 ? <PopOver.Label>{label}</PopOver.Label> : null,
                        }}
                        maxHeight={SUGGESTION_MAX_HEIGHT}
                    />
                </div>
            )}
        </div>
    );
}

export default LinkToPageDropDown;
